import { Controller } from "@hotwired/stimulus"
import {
  createDoughnutChart,
  createHorizontalBarChart,
  comparisonBarDatasets,
  destroyChart
} from "../modules/money_smart/chart_helpers"
import {
  renderBudgetBreakdown,
  renderInsights
} from "../modules/money_smart/budget_helpers"

export default class extends Controller {
  static targets = [
    "form", "monthlyIncome",
    "housing", "food", "transport", "utilities",
    "entertainment", "insurance", "healthcare", "personal",
    "submitButton", "results", "errors", "errorMessage",
    "totalSpending", "remaining", "savingsRate", "incomeBracket",
    "pieChart", "comparisonChart",
    "breakdownBody", "insightsContainer", "printButton"
  ]

  static values = { calculateUrl: String, autoCalculate: Boolean }

  pieChart = null
  comparisonChart = null

  connect() {
    this.prefillFromUrl()
    if (this.autoCalculateValue && !new URLSearchParams(window.location.search).size) {
      this.formTarget.requestSubmit()
    }
  }

  disconnect() {
    destroyChart(this.pieChart)
    destroyChart(this.comparisonChart)
    this.pieChart = null
    this.comparisonChart = null
  }

  async calculate(event) {
    event.preventDefault()
    this.hideErrors()

    const body = {
      monthly_income: this.monthlyIncomeTarget.value,
      housing: this.housingTarget.value,
      food: this.foodTarget.value,
      transport: this.transportTarget.value,
      utilities: this.utilitiesTarget.value,
      entertainment: this.entertainmentTarget.value,
      insurance: this.insuranceTarget.value,
      healthcare: this.healthcareTarget.value,
      personal: this.personalTarget.value
    }

    this.submitButtonTarget.disabled = true

    try {
      const response = await fetch(this.calculateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        this.showErrors(data.errors || ["Calculation failed"])
        return
      }

      this.renderResults(data)
      this.updateUrl(body)
    } catch {
      this.showErrors(["Something went wrong. Please try again."])
    } finally {
      this.submitButtonTarget.disabled = false
    }
  }

  renderResults(data) {
    this.totalSpendingTarget.textContent = this.formatMoney(data.total_spending)
    this.remainingTarget.textContent = this.formatMoney(data.remaining)
    this.savingsRateTarget.textContent = data.remaining_percentage + "%"
    this.incomeBracketTarget.textContent = data.income_bracket

    renderBudgetBreakdown(this.breakdownBodyTarget, data.category_breakdown, this.formatMoney)
    renderInsights(this.insightsContainerTarget, data.summary_insights)
    this.renderCharts(data)

    this.resultsTarget.classList.remove("hidden")
  }

  async renderCharts(data) {
    destroyChart(this.pieChart)
    destroyChart(this.comparisonChart)
    this.pieChart = null
    this.comparisonChart = null

    this.pieChart = await createDoughnutChart(
      this.pieChartTarget,
      data.pie_chart_data.labels,
      data.pie_chart_data.data
    )

    const datasets = comparisonBarDatasets(data.comparison_chart_data)
    this.comparisonChart = await createHorizontalBarChart(
      this.comparisonChartTarget,
      data.comparison_chart_data.labels,
      datasets
    )
  }

  print() {
    window.print()
  }

  updateUrl(params) {
    const url = new URL(window.location)
    Object.entries(params).forEach(([key, val]) => {
      if (val !== "" && val !== null && val !== undefined) {
        url.searchParams.set(key, val)
      } else {
        url.searchParams.delete(key)
      }
    })
    window.history.replaceState({}, "", url)
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    if (params.size === 0) return

    const fields = {
      monthly_income: this.monthlyIncomeTarget,
      housing: this.housingTarget,
      food: this.foodTarget,
      transport: this.transportTarget,
      utilities: this.utilitiesTarget,
      entertainment: this.entertainmentTarget,
      insurance: this.insuranceTarget,
      healthcare: this.healthcareTarget,
      personal: this.personalTarget
    }

    let hasValues = false
    Object.entries(fields).forEach(([key, el]) => {
      const val = params.get(key)
      if (val !== null) {
        el.value = val
        hasValues = true
      }
    })

    if (hasValues) {
      this.formTarget.requestSubmit()
    }
  }

  showErrors(messages) {
    this.resultsTarget.classList.add("hidden")
    this.errorMessageTarget.textContent = messages.join(". ")
    this.errorsTarget.classList.remove("hidden")
  }

  hideErrors() {
    this.errorsTarget.classList.add("hidden")
  }

  formatMoney(value) {
    return "$" + Number(value).toLocaleString("en-NZ", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
